import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Modal } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faXmark, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { OrderItem } from '../../../components/OrderItem';
import tw from 'twin.macro';
import { CloudUploadIcon, XIcon } from '@heroicons/react/outline';
import { gql, useMutation } from '@apollo/client';
import { ORDER_FIELDS } from '../../../apollo/fragments';
import { UPLOAD_FILE_TO_S3 } from '../../../apollo/queries';
import { uploadToS3WithPresignedUrl } from '../../../utils/s3Upload';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import $ from 'jquery';
import GenericSidebar from '../../../components/GenericSidebar';
import {Loading} from '../../../components/Loading'


// GraphQL mutations
const UPDATE_ORDER_ITEMS = gql`
  ${ORDER_FIELDS}
  mutation reviewOrderItems($orderId: String!, $items: [reviewItem!]!) {
    reviewOrderItems(orderId: $orderId, items: $items) {
      status
      message
      execution_time
      order {
        ...OrderFields
      }
    }
  }
`;

const CREATE_META_FIELD = gql`
  ${ORDER_FIELDS}
  mutation updateOrderItemMetaDataByOrderNumber(
    $order_number: String!
    $itemIndex: Int!
    $key: String!
    $value: String!
  ) {
    updateOrderItemMetaDataByOrderNumber(
      order_number: $order_number,
      itemIndex: $itemIndex,
      key: $key,
      value: $value
    ) {
      status
      message
      execution_time
      order {
        ...OrderFields
      }
    }
  }
`;

const MOVE_ORDER_FOLDER = gql`
  mutation changeOrderFolder($orderID: String!) {
    changeOrderFolder(orderID: $orderID) {
      status
      message
      folder_id
    }
  }
`;

const UpdateProof = ({ state, setStorageState }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [orderItems, setOrderItems] = useState(state?.order?.order_items || []);
  const [dropdownValue, setDropdownValue] = useState(""); // Empty string as initial value
  const [updates, setUpdates] = useState({}); // Object to store updates for each item
  const [folder_id, setFolderID] = useState(null) 
  const [reviewSidebarOpen, setReviewSidebarOpen] = useState(false);
  const [updatedOrder, setUpdatedOrder] = useState(null);
  const [hasValidProofs, setHasValidProofs] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const [createMetaField] = useMutation(CREATE_META_FIELD);
  const [uploadFileToS3] = useMutation(UPLOAD_FILE_TO_S3);
  const [moveOrderFolder] = useMutation(MOVE_ORDER_FOLDER, {
    onCompleted: data => {
        const updatedFolderId = data.changeOrderFolder.folder_id;
        if (data.changeOrderFolder.status === 'success' && updatedFolderId) {
          setStorageState(prevState => ({
              ...prevState,
              order: {
              ...prevState.order,
              folder_id: updatedFolderId
              },
          }));
          $("#collapseReviewProofs").removeClass('show')
        }
        setIsProcessing(false);
        toastr.success('Proof updated successfully');
        console.log("move completed")
      },
      onError: (error) => {
        console.error('Error moving orders:', error);
        toastr.error('Something went wrong!');
        setIsProcessing(false);
      }
    });
  const [updateOrderItems] = useMutation(UPDATE_ORDER_ITEMS,{
    onCompleted: data => {
        setIsProcessing(false);
        if (data.reviewOrderItems.status === 'success') {
          setUpdatedOrder(data.reviewOrderItems.order);
          setStorageState(prevState => ({
              ...prevState,
              order: {
              ...prevState.order,
              folder_id: data.reviewOrderItems.order.folder_id
              },
          }));
          $("#collapseReviewProofs").removeClass('show')
          toastr.success('Proof submitted successfully');
        }
        else if(data.reviewOrderItems.status === 'line_item_updated'){
          toastr.success('Proof updated successfully');
        }
        else if(data.reviewOrderItems.status === 'wrong_folder'){
          toastr.warning("Proofs were not submitted. Your order status has been updated. We're updating your page now.");
          setTimeout(() => window.location.reload(), 4000);
        }
      },
      onError: (error) => {
        toastr.error('Something went wrong!');
        console.error('Error submitting proof:', error);
        setIsProcessing(false);
      }
    });
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  useEffect(() => {
    if (!selectedItem) return;

    const newPhoto = updates[selectedItem?.id]?.newPhoto;
    const newCrop = updates[selectedItem?.id]?.newCrop;
    const otherCropDetails = updates[selectedItem?.id]?.otherCropDetails;
  
    const shouldDisable =
      !dropdownValue || // No selection
      (dropdownValue === "reject" &&
        !newPhoto || // If newPhoto is null, disable Save
        !newCrop) || // No crop selected
      (newCrop === "other" && !otherCropDetails?.trim()) || // No crop details when selecting "Other"
      (dropdownValue === "reject" &&
        newPhoto === (selectedItem?.variation_list?.find(meta => meta.key === "_Uploaded Image (direct link)")?.value || "") &&
        newCrop === updates[selectedItem?.id]?.defaultNewCrop &&
        otherCropDetails === updates[selectedItem?.id]?.defaultOtherCropDetails); // Crop details reverted to original
  
    setIsSaveDisabled(shouldDisable);
  }, [dropdownValue, updates, selectedItem]); // Run effect whenever dropdownValue, updates, or selectedItem changes
    
  useEffect(() => {
    // Filter to only consider "Custom Air Freshener" items
    const customAirFreshenerItems = orderItems.filter(item => {
      return item.name?.includes('Custom Air Freshener') || false;
    });

    const valid = customAirFreshenerItems.length > 0 && customAirFreshenerItems.every((item) => {
      const updateProofStatus = updates[item.id]?.proofStatus;
      const metaProofStatus = item.metadata?.find((meta) => meta.key === 'Proof')?.value;
      const effectiveProofStatus = updateProofStatus || metaProofStatus;
  
      return ['accepted', 'rejected'].includes(effectiveProofStatus);
    });
  
    setHasValidProofs(valid);
  }, [orderItems, updates]);  

  useEffect(() => {
    setOrderItems(state?.order?.order_items || []);
  }, [state]);

  const handleSave = () => {
    if (dropdownValue && selectedItem) {
      const newUpdates = {
        proofStatus: dropdownValue === 'accept' ? 'accepted' : 'rejected',
        newPhoto: updates[selectedItem.id]?.newPhoto || null,
        newCrop: updates[selectedItem.id]?.newCrop || '',
        otherCropDetails: updates[selectedItem.id]?.otherCropDetails || '',
      };

      setUpdates((prev) => ({
        ...prev,
        [selectedItem.id]: newUpdates,
      }));

      setReviewSidebarOpen(false) // Close sidebar after saving
    }
  };

  const handleSubmit = async () => {
    setIsProcessing(true); // Start spinner + disable button
    // Filter to only process "Custom Air Freshener" items
    const customAirFreshenerItems = orderItems.filter(item => {
      return item.name?.includes('Custom Air Freshener') || false;
    });

    const updatedItems = customAirFreshenerItems.map((item, itemIndex) => {
      if (updates[item.id]) {
        updates[item.id].proofStatus === 'accepted' ? 'approved' : 'rejected'
  
        return {
            id: item.id,
            crop: updates[item.id].newCrop === 'other'
            ? updates[item.id].otherCropDetails.trim()  // Use custom text as crop
            : updates[item.id].newCrop || '',
            customCrop: updates[item.id].newCrop === 'other' ? updates[item.id].otherCropDetails.trim() : '',
            image: updates[item.id].newPhoto || '',
            proof: updates[item.id].proofStatus
          };
      }
      return null;
    }).filter(item => item !== null);
  
    try {
      await updateOrderItems({
        variables: {
          orderId: state.order.id,
          items: updatedItems,
        },
      });
    } catch (error) {
      toastr.error('Error updating proof');
      console.error('Error in handleSubmit:', error);
    }
  };
  

  const handleReviewClick = (item) => {
    setSelectedItem(item);
    setDropdownValue(''); // Reset dropdown value when opening modal
  
    // Find the current default values from item metadata
    const variation_crop = item.variation_list?.find(meta => meta.key === 'Crop')?.value;
    const defaultPhoto = item.variation_list?.find(meta => meta.key === '_Uploaded Image (direct link)')?.value || '';
    const defaultCrop = !["Just The Head(s)", "Entire Body(s)", "Do Not Crop", "Just The Car"].includes(variation_crop)
      ? "other"
      : variation_crop; // If it’s one of the predefined, no other crop details
    const defaultOtherCrop = !["Just The Head(s)", "Entire Body(s)", "Do Not Crop", "Just The Car"].includes(defaultCrop)
      ?  variation_crop // This becomes the "Other Crop Details"
      : ''; // If it’s one of the predefined, no other crop details
  
    // Set default values in the updates state
    setUpdates((prev) => ({
      ...prev,
      [item.id]: {
        newPhoto: defaultPhoto,
        newCrop: defaultCrop,
        otherCropDetails: defaultOtherCrop,
        defaultNewCrop: defaultCrop, // Store default new crop value
        defaultOtherCropDetails: defaultOtherCrop, // Store default other crop details
      }
    }));
  
    // setModalOpen(true); // Open the modal
    setReviewSidebarOpen(true); // Open the sidebar
  };
  

  const handlePhotoChange = (url, remove = false) => {
    setUpdates((prev) => {
      const updatedItem = {
        ...prev[selectedItem.id],
        newPhoto: remove ? null : url, // If removing, set newPhoto to null
        isRemoved: remove, // Track removal status
      };
    
      return {
        ...prev,
        [selectedItem.id]: updatedItem,
      };
    });
  };

  const handleCropChange = (event) => {
    const newCropValue = event.target.value;
    const defaultCrop = selectedItem?.variation_list?.find(meta => meta.key === 'Crop')?.value || '';
  
    setUpdates((prev) => ({
      ...prev,
      [selectedItem.id]: {
        ...prev[selectedItem.id],
        newCrop: newCropValue,
        // If crop value is not "other", reset otherCropDetails to empty string
        otherCropDetails:
          newCropValue === 'other'
            ? !["Just The Head(s)", "Entire Body(s)", "Do Not Crop", "Just The Car"].includes(defaultCrop)
              ? defaultCrop // Show the default crop value if it's not one of the predefined options
              : '' // Otherwise, set to empty string
            : '', // If any crop other than "other" is selected, reset otherCropDetails to an empty string
      },
    }));
  };
  

  const handleOtherCropDetailsChange = (event) => {
    const newOtherCropDetails = event.target.value; // Do not trim here to allow users to fully delete text
    
    setUpdates((prev) => ({
      ...prev,
      [selectedItem.id]: {
        ...prev[selectedItem.id],
        otherCropDetails: newOtherCropDetails, // Directly set the value, including empty string
      },
    }));
  };  

  const uploadPhoto = useCallback(async (file) => {
    try {
      const result = await uploadFileToS3({
        variables: { 
          folderKey: 'proofs', 
          fileName: `${selectedItem.id}-${file.name}`,
          contentType: file.type || 'application/octet-stream'
        }
      });
      
      if (result.data?.uploadFileToS3?.status === 'success') {
        const { uploadUrl, url: s3Url } = result.data.uploadFileToS3
        await uploadToS3WithPresignedUrl(file, uploadUrl);

        handlePhotoChange(s3Url);
      } else {
        console.log('Error uploading photo:', result.data?.uploadFileToS3?.message);
      }
    } catch (error) {
      console.log('Error uploading photo:', error);
    }
  }, [selectedItem, uploadFileToS3]);

  const onDrop = useCallback((acceptedFiles) => {
    acceptedFiles.forEach(file => {
      const reader = new FileReader();
      uploadPhoto(file);
      reader.onload = () => {
        console.log('File uploaded');
      };
      reader.readAsDataURL(file);
    });
  }, [uploadPhoto]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const { order } = state;

  if (!order) return null;

  // Filter to only show "Custom Air Freshener" items
  const filteredItems = orderItems.filter(item => {
    return item.name?.includes('Custom Air Freshener') || false;
  });

  return (
    <div className="item-box px-0">
      <div>
        {filteredItems.map((item) => {
          const proofMetadata = item.metadata.find(meta => meta.key === 'Proof');
          const rawProofStatus = updates[item.id]?.proofStatus || proofMetadata?.value;
          const proofStatus = ['accepted', 'mockup', 'subscription', 'reorder'].includes(rawProofStatus) ? 'accepted' : rawProofStatus;

          return (
            <div
              className={`item-outer ${
                proofStatus
                  ? proofStatus === 'accepted'
                    ? 'border-success'
                    : proofStatus === 'rejected'
                    ? 'border-red'
                    : 'border-gray'
                  : 'border-gray'
              }`}
              key={item.id}
            >
              <div className="edit-items-box edit-items-box-inner mt-2 -mx-2 row flex flex-wrap relative" tw="justify-between !px-3 !py-3 md:!px-5 md:!py-5">
              {proofStatus && (
                <div className={`banner-text proof-banner ${proofStatus === 'accepted' ? 'banner-success' : proofStatus === 'rejected' ? 'banner-rejected' : ''}`}>
                  {proofStatus === 'accepted' && 'Approved'}
                  {proofStatus === 'rejected' && 'Rejected'}
                  {/* {proofStatus === 'pending' && 'Previously Rejected, Review New Proof'} */}
                </div>
              )}

                <div className="d-flex-item card-content" tw="px-0"
                  // style={{ marginTop: proofStatus ? '15px' : undefined }}
                >
                  <OrderItem
                    key={`${item.id}-${updates[item.id]?.proofStatus || item.metadata.find(meta => meta.key === 'Proof')?.value || 'none'}`}
                    item={item}
                    states={orderItems}
                    setOrderItems={setOrderItems}
                    hasPrice={false}
                    shQuantity={false}
                    showQuantity={false}
                    referenceSection="review_proofs"
                    proofStatus={updates[item.id]?.proofStatus || item.metadata.find(meta => meta.key === 'Proof')?.value}
                  />
                </div>
                <div onClick={() => handleReviewClick(item)} className="text-right w-sm-100" tw="px-0">
                  <div className="edit-btn" tw="mb-0!">
                    <button className="btn brand-btn edit-btn review-submit-btn w-sm-100" tw="mb-0!" style={{minWidth: "151px"}}>Review</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Submit button centered below the item list */}
      <div className="text-center">
      <button 
        className="btn brand-btn submit-btn" 
        disabled={!hasValidProofs || isProcessing}
        onClick={handleSubmit}
        style={isProcessing ? { display: 'inline-flex', justifyContent: 'center', alignItems: 'center' } : {}}
      >
        {isProcessing ? (
          <>
            Submitting...
            <Loading tw="ml-2" />
          </>
        ) : (
          'Submit'
        )}
      </button>

      </div>


      <GenericSidebar
        title="Review Proof"
        icon={<FontAwesomeIcon icon={faEye} />}
        isOpen={reviewSidebarOpen}
        onClose={() => setReviewSidebarOpen(false)}
        footer={
          <button
            className="btn edit-btn brand-btn"
            tw="!max-w-full w-full"
            css={{ height: '50px', borderRadius: '3px'}}
            onClick={handleSave}
            disabled={isSaveDisabled}
          >
            Save
          </button>
        }        
      >
        <div tw="flex flex-col">
          <div className="modal-content-box edit-item-popup" tw="flex-1">
            <div className="modal-body pt-0 px-0">
              {/* Proof Image Section */}
              <div className="mb-3">
                {/* <h3 tw="text-lg text-brand mb-2">Proof</h3> */}
                {selectedItem ? (
                  (() => {
                    const jpgMetadata = selectedItem.metadata.find((meta) => meta.key === 'Preview');
                    return jpgMetadata ? (
                      <img
                        src={jpgMetadata.value}
                        alt="Proof Image"
                        style={{ maxWidth: '100%', objectFit: 'contain', borderRadius: '5px' }}
                      />
                    ) : (
                      <p>No image available for {selectedItem.name}</p>
                    );
                  })()
                ) : (
                  <p>No image available for {selectedItem?.name}</p>
                )}
              </div>

              {/* Feedback Section */}
              <div tw="sm:mt-0">
                  <div className="">
                    {/* <h3 tw="text-lg text-brand">What do you think?</h3> */}

                    <div className="flex flex-col w-full">
                      <label
                        tw="cursor-pointer w-full flex items-center p-3 border"
                        css={{
                          border: dropdownValue === 'accept' ? '2px solid #3BA25F' : '2px solid #B3B3B3',
                          borderTopLeftRadius: '5px',
                          borderTopRightRadius: '5px',
                          borderBottom: dropdownValue === 'accept'
                            ? '2px solid #3BA25F'
                            : dropdownValue === 'reject'
                            ? '0'
                            : '2px solid #B3B3B3',
                          backgroundColor: dropdownValue === 'accept' ? '#D0FFD9' : 'transparent',
                          color: '#3BA25F',
                          fontSize: '16px',
                          fontWeight: 400,
                          marginBottom: 0,
                          borderWidth: '2px'
                        }}
                      >
                        <input
                          type="radio"
                          name="proof-selection"
                          value="accept"
                          checked={dropdownValue === 'accept'}
                          onChange={() => setDropdownValue('accept')}
                          css={{
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            MozAppearance: 'none',
                            width: '14px',
                            height: '14px',
                            borderRadius: '50%',
                            marginRight: '8px',
                            border: dropdownValue === 'accept' ? '' : '1px solid #B3B3B3',
                            backgroundColor: '#fff',
                            display: 'inline-block',
                            position: 'relative',
                            boxSizing: 'border-box',
                            outline: dropdownValue === 'accept' ? '4px solid #3BA25F' : 'none',
                            outlineOffset: dropdownValue === 'accept' ? '-4px' : '0',
                          }}
                        />
                        Looks Good (approve)
                      </label>

                      <label
                      tw="cursor-pointer w-full flex items-center p-3 border"
                      css={{
                        border: dropdownValue === 'reject' ? '2px solid #D20000' : '2px solid #B3B3B3',
                        borderTop: dropdownValue === 'reject' ? '2px solid #D20000' : '0',
                        borderBottomLeftRadius: dropdownValue === 'reject' ? '0' : '5px',
                        borderBottomRightRadius: dropdownValue === 'reject' ? '0' : '5px',
                        backgroundColor: dropdownValue === 'reject' ? '#FFD2D2' : 'transparent',
                        color: '#D20000',
                        fontSize: '16px',
                        fontWeight: 400,
                        marginTop: '0',
                        marginBottom: 0,
                      }}
                    >
                      <input
                        type="radio"
                        name="proof-selection"
                        value="reject"
                        checked={dropdownValue === 'reject'}
                        onChange={() => setDropdownValue('reject')}
                        css={{
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          width: '14px',
                          height: '14px',
                          borderRadius: '50%',
                          marginRight: '8px',
                          border: dropdownValue === 'reject' ? '' : '2px solid #D20000',
                          backgroundColor: '#fff',
                          display: 'inline-block',
                          position: 'relative',
                          outline: dropdownValue === 'reject' ? '4px solid #D20000' : 'none',
                          outlineOffset: dropdownValue === 'reject' ? '-4px' : '0',
                        }}
                      />
                        Needs Revisions (reject)
                      </label>
                    </div>
                  </div>
              </div>

              {dropdownValue === 'reject' && (
                <div
                  css={{
                    border: '2px solid #B3B3B3',
                    borderTop: 'none',
                    borderBottomLeftRadius: '5px',
                    borderBottomRightRadius: '5px',
                    padding: '16px',
                    overflowY: 'auto',
                  }}
                  tw="mb-[58px] md:mb-[46px]"
                >
                  {/* New Photo Section */}
                  <div className="mb-4">
                    <h3 tw="text-lg text-brand">New Photo</h3>
                    <div
                      className="img-crop-box h-100 border-gray"
                      {...getRootProps()}
                      css={[
                        tw`h-full relative border-dashed flex items-center justify-center p-6 bg-white text-sm text-brand text-center`,
                        isDragActive && tw`border-brand`,
                      ]}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '0',
                        borderWidth: '0'

                      }}
                    >
                      <input {...getInputProps()} />

                      {updates[selectedItem?.id]?.newPhoto && !updates[selectedItem?.id]?.isRemoved ? (
                        /* If new photo exists and is not removed, display it */
                        <>
                          <img
                            src={updates[selectedItem.id]?.newPhoto}
                            alt="New picture"
                            tw="max-w-full max-h-full object-contain"
                            width="400"
                            height="260"
                            className="upload-box-image"
                          />
                          <div className="upload-image-input-box">
                            <span className="upload-image-input-text">
                              Upload New Photo
                            </span>
                          </div>

                          {/* Delete Button: Clicking removes image and shows upload section */}
                          {/* {dropdownValue === 'reject' && (
                            <button
                              type="reset"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevents file selection from opening
                                handlePhotoChange(null, true); // Mark photo as removed
                              }}
                              aria-label="Delete Image"
                            >
                              <XIcon tw="absolute top-2 right-2 w-5 h-5" style={{top: "-25px"}} />
                            </button>
                          )} */}
                        </>
                      ) : !updates[selectedItem?.id]?.isRemoved &&
                        selectedItem?.variation_list?.find(meta => meta.key === '_Uploaded Image (direct link)')?.value ? (
                        /* If no new photo, fallback to Uploaded Image (only if not removed) */
                        <img
                          src={selectedItem?.variation_list?.find(meta => meta.key === '_Uploaded Image (direct link)')?.value}
                          alt="Uploaded Image"
                          tw="max-w-full max-h-full object-contain"
                          width="400"
                          height="260"
                          className="upload-box-image"
                        />
                      ) : (
                        /* If no photo exists or was removed, show the upload section */
                        <div>
                          <CloudUploadIcon css={[tw`h-12 w-12 mx-auto text-brand`]} />
                          <p tw="mt-2">
                            <span className="semi-bold text-size">Upload Photo</span> <br />
                            <span className="text-gray">Drag & Drop or <u>Browse</u></span>
                          </p>
                        </div>
                      )}

                    </div>
                    {/* {updates[selectedItem?.id]?.newPhoto && (
                      <button type="reset" onClick={() => handlePhotoChange(null)}>
                        <XIcon tw="absolute top-2 right-2 w-5 h-5" />
                      </button>
                    )} */}
                  </div>

                  {/* New Crop Section */}
                  <div
                    className={
                      updates[selectedItem?.id]?.newCrop === 'other' ||
                      !["Just The Head(s)", "Entire Body(s)", "Do Not Crop", "Just The Car"].includes(
                        updates[selectedItem?.id]?.newCrop || selectedItem?.variation_list?.find(meta => meta.key === 'Crop')?.value
                      )
                        ? 'mb-4'
                        : ''
                    }
                  >
                    <h3 tw="text-lg text-brand">New Crop</h3>
                    <div className="select-box">
                      <span className="select-icon">
                        <i className="fa fa-caret-down"></i>
                      </span>
                      {/* Logic to determine whether to select "other" */}
                      <select
                        value={
                          // Check if the current crop matches one of the predefined options
                          ["Just The Head(s)", "Entire Body(s)", "Do Not Crop", "Just The Car"].includes(updates[selectedItem?.id]?.newCrop || selectedItem?.variation_list?.find(meta => meta.key === 'Crop')?.value)
                            ? updates[selectedItem?.id]?.newCrop || selectedItem?.variation_list?.find(meta => meta.key === 'Crop')?.value
                            : 'other' // If not, select "other"
                        }
                        onChange={handleCropChange}
                        tw="h-full w-full p-2 bg-white focus:ring-brand rounded-md"
                        style={{color: "#0D2D46", border: "2px solid #B3B3B3", borderRadius: '5px'}}
                        >
                        <option value="" disabled>
                          Select your crop option
                        </option>
                        <option value="Just The Head(s)">Just The Head(s)</option>
                        <option value="Entire Body(s)">Entire Body(s)</option>
                        <option value="Do Not Crop">Do Not Crop</option>
                        <option value="Just The Car">Just The Car</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  {/* Other Crop Details */}
                  {updates[selectedItem?.id]?.newCrop === 'other' || (
                    !["Just The Head(s)", "Entire Body(s)", "Do Not Crop", "Just The Car"].includes(
                      updates[selectedItem?.id]?.newCrop || selectedItem?.variation_list?.find(meta => meta.key === 'Crop')?.value
                    )
                  ) ? (
                    <div className="">
                      <h3 tw="text-lg text-brand">Other</h3>
                      <div className="info-box">
                        <span><FontAwesomeIcon icon={faCircleInfo} className="icon"/>Do not reference your current design proof in the text box below</span>
                      </div>
                      <input
                        type="text"
                        className="input-styling"
                        tw="h-full w-full p-2 bg-white focus:ring-brand"
                        value={
                          updates[selectedItem?.id]?.otherCropDetails || ''
                        }
                        onChange={handleOtherCropDetailsChange}
                      />
                    </div>
                  ) : null}

                </div>
              )}

              {/* Save Button */}
              {/* <div className="text-center mt-2">
                <button
                  className="btn edit-btn brand-btn"
                  tw="!max-w-full w-full"
                  onClick={handleSave}
                  disabled={isSaveDisabled}
                >
                Submit
                </button>
              </div> */}

            </div>
          </div>
        </div>
        {/* Sticky Submit Button at Bottom */}
        {/* <div tw="bg-white pb-4"  
            css={{ position: 'sticky', bottom: 0, zIndex: 10, borderTop: '1px solid #B3B3B3' }}>
          <button className="btn edit-btn brand-btn" tw="!max-w-full w-full"
            css={{height: '50px', borderRadius: '3px', marginTop: '10px'}}
            onClick={handleSave} disabled={isSaveDisabled}>
            Save
          </button>
        </div> */}
      </GenericSidebar>
      {/* </Modal> */}
    </div>
  );
};

export default UpdateProof;
